import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Form, Spin} from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import InputMoney from "./InputMoney";
import {getCreditCards, oneTimePayment} from "../../helpers/creditCard";
import {messageType, showMessages} from "../../helpers/message";
import {creditcardPayment, noCreditCardOnFile, greaterThanOrEqualOne} from "../../helpers/text";
import StoredCreditCardForm from "./StoredCreditCardForm";

const ChargeCreditCardFormStaff = ({toggleRefreshBalances, refetchStoredCard, setRefetchStoredCard}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const [loading, setLoading] = useState(true);
    const [creditCard, setCreditCard] = useState(null);

    // region form functions
    const [form] = Form.useForm();

    const handleFinish = async (values) => {
        const amount = values['Amount'];
        let expiryDateString = null;

        try {
            setLoading(true);

            const abortSignal = abortSignalRef.current;
            await oneTimePayment(amount, null, expiryDateString, null, true, abortSignal);

            showMessages('Payment has successfully been applied to account.', messageType.success);
            toggleRefreshBalances(true);
            setLoading(false);
            form.resetFields();
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
        }
    };

    //endregion

    const fetchStoredCC = useCallback(async (abortSignal) => {
        try {
            const response = await getCreditCards({}, abortSignal);

            // For now only single cc can be stored
            const creditCards = response.data;
            let creditCard = null;

            if (creditCards.length > 0) {
                creditCard = creditCards[0];
                const expiryDate = dayjs(creditCard.expiry).tz();
                creditCard.expiry = expiryDate.format('MM') + ' / ' + expiryDate.format('YY');
                creditCard.type = creditCard.type.toUpperCase();

                setCreditCard(creditCard);
            } else {
                setCreditCard(null);
            }

            setLoading(false);
        } catch (e) {
            showMessages(e.message, messageType.error);
            setLoading(false);
            if(e.status_code === 404) window.location.replace('/404');
        }
    }, []);

    useEffect(() => {
        if (refetchStoredCard === true) {
            const abortSignal = abortSignalRef.current;
            fetchStoredCC(abortSignal);
            setRefetchStoredCard(false);
        }
    }, [refetchStoredCard, setRefetchStoredCard, fetchStoredCC]);

    useEffect(() => {
        const abortSignal = abortSignalRef.current;
        if (creditCard === null) {
            fetchStoredCC(abortSignal);
        }
    }, [creditCard, fetchStoredCC]);

    // Cleanup effect when component unmounts
    useEffect(() => {
        const abortController = abortControllerRef.current;
        return function abort() {
            abortController.abort();
        }
    }, []);

    return (
        <Spin spinning={loading}>
            {
                creditCard &&
                <>
                    {creditcardPayment}
                    <Form
                        form={form}
                        layout="vertical"
                        name="charge-credit-card"
                        onFinish={handleFinish}
                    >
                        <Form.Item
                            name="Amount"
                            label="Amount"
                            rules={[{type: 'number', required: true, min: 1}]}
                            help={greaterThanOrEqualOne}
                        >
                            <InputMoney />
                        </Form.Item>
                        <StoredCreditCardForm creditCard={creditCard}/>
                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                Complete Payment
                            </Button>
                        </Form.Item>
                    </Form>
                </>
            }
            {
                !creditCard &&
                <div className="empty-data-table">
                    {noCreditCardOnFile}
                </div>
            }
        </Spin>
    );
};

export default ChargeCreditCardFormStaff;

ChargeCreditCardFormStaff.propTypes = {
    toggleRefreshBalances: PropTypes.func.isRequired,
    refetchStoredCard: PropTypes.bool.isRequired,
    setRefetchStoredCard: PropTypes.func.isRequired,
};
